import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Fab,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import {
  Add as AddIcon,
  Business as BusinessIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FolderOpen as FolderOpenIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { NewProjectDialog, type ProjectFormData } from './NewProjectDialog'
import type { Project } from '@/interfaces'

export interface ProjectDashboardProps {
  projects: Project[]
  loading: boolean
  error: string | null
  organizationId: string | null
  onRefresh: () => void
  onCreateProject: (formData: ProjectFormData) => void
  onDeleteProject: (projectId: string) => void
  onEditProject?: (project: Project) => void
  onOpenProject: (projectId: string) => void
  
  // Dialog state - could be external but keeping internal for simplicity
  createProjectLoading?: boolean
  createProjectError?: string | null
}

interface ProjectCardProps {
  project: Project
  onEdit: (project: Project) => void
  onDelete: (projectId: string) => void
  onOpenProject: (projectId: string) => void
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onEdit, onDelete, onOpenProject }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleEdit = () => {
    onEdit(project)
    handleMenuClose()
  }

  const handleDelete = () => {
    onDelete(project.id)
    handleMenuClose()
  }

  const handleOpenProject = () => {
    onOpenProject(project.id)
  }

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            {project.name}
          </Typography>
          <IconButton 
            size="small" 
            onClick={handleMenuOpen}
            sx={{ ml: 1 }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleEdit}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Project</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete Project</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
        
        {project.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {project.description}
          </Typography>
        )}
        
        <Typography variant="caption" color="text.secondary">
          Created: {new Date(project.createdAt).toLocaleDateString()}
        </Typography>
        {project.updatedAt !== project.createdAt && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Updated: {new Date(project.updatedAt).toLocaleDateString()}
          </Typography>
        )}
      </CardContent>
      
      <CardActions>
        <Button 
          size="small" 
          startIcon={<FolderOpenIcon />}
          onClick={handleOpenProject}
          sx={{ ml: 'auto' }}
        >
          Open Project
        </Button>
      </CardActions>
    </Card>
  )
}

export const ProjectDashboard: React.FC<ProjectDashboardProps> = ({
  projects,
  loading,
  error,
  organizationId,
  onRefresh,
  onCreateProject,
  onDeleteProject,
  onEditProject = () => console.log('Edit not implemented'),
  onOpenProject,
  createProjectLoading = false,
  createProjectError = null,
}) => {
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'name' | 'created' | 'updated'>('updated')
  const [isNewProjectDialogOpen, setIsNewProjectDialogOpen] = useState(false)

  // Show organization requirement if not in organization
  if (!organizationId) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 3 }} />
        <Typography variant="h4" gutterBottom>
          Organization Required
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          Projects in Wave are organization-scoped to ensure proper access control and collaboration. 
          You must be a member of an organization to create and manage projects.
        </Typography>
        <Alert severity="info" sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}>
          Use the **organization switcher** in the top navigation to create a new organization or join an existing one.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>To get started:</strong>
        </Typography>
        <Box component="ol" sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto', pl: 2 }}>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Click the organization switcher in the top navigation bar
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select "Create Organization" to start your own organization
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Or ask an admin to invite you to their organization
          </Typography>
        </Box>
      </Box>
    )
  }

  // Filter and sort projects whenever projects, searchTerm, or sortBy changes
  useEffect(() => {
    let filtered = [...projects]

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(project => 
        project.name.toLowerCase().includes(term) ||
        (project.description && project.description.toLowerCase().includes(term))
      )
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'created':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        case 'updated':
        default:
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      }
    })

    setFilteredProjects(filtered)
  }, [projects, searchTerm, sortBy])

  const handleCreateProject = async (formData: ProjectFormData) => {
    await onCreateProject(formData)
    // Only close dialog if creation was successful (no error)
    if (!createProjectError) {
      setIsNewProjectDialogOpen(false)
    }
  }

  const handleCloseDialog = () => {
    setIsNewProjectDialogOpen(false)
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={onRefresh}>
          Try Again
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Projects
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsNewProjectDialogOpen(true)}
          size="large"
        >
          New Project
        </Button>
      </Box>

      {/* Search and Filter Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search projects..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
          }}
          sx={{ flexGrow: 1, minWidth: 300 }}
        />
        
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={sortBy}
            label="Sort by"
            onChange={(e) => setSortBy(e.target.value as 'name' | 'created' | 'updated')}
          >
            <MenuItem value="updated">Recently Updated</MenuItem>
            <MenuItem value="created">Recently Created</MenuItem>
            <MenuItem value="name">Name (A-Z)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          {projects.length === 0 ? (
            <>
              <Typography variant="h6" gutterBottom>
                No projects yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first project to get started with Wave
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsNewProjectDialogOpen(true)}
                size="large"
              >
                Create Your First Project
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>
                No projects match your search
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search terms or filters
              </Typography>
            </>
          )}
        </Box>
      ) : (
        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
            gap: 3 
          }}
        >
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onEdit={onEditProject}
              onDelete={onDeleteProject}
              onOpenProject={onOpenProject}
            />
          ))}
        </Box>
      )}

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
        }}
        onClick={() => setIsNewProjectDialogOpen(true)}
      >
        <AddIcon />
      </Fab>

      {/* New Project Dialog */}
      <NewProjectDialog
        open={isNewProjectDialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleCreateProject}
        loading={createProjectLoading}
        error={createProjectError}
        organizationId={organizationId}
      />
    </Box>
  )
} 